// src/scripts/stable-hash-scroll.ts

type StableHashScrollOptions = {
  timeoutMs?: number;
  tolerancePx?: number;
};

const SCROLL_KEYS = new Set(['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' ']);

function getIdFromHash(hash: string): string | null {
  const encodedId = hash.replace(/^#/, '');

  if (!encodedId) return null;

  try {
    return decodeURIComponent(encodedId);
  } catch {
    return encodedId;
  }
}

export function scrollToHashWhenStable(
  hash: string,
  { timeoutMs = 3000, tolerancePx = 1 }: StableHashScrollOptions = {}
): () => void {
  const id = getIdFromHash(hash);

  if (!id) {
    return () => undefined;
  }

  let frameId = 0;
  let stopped = false;
  let previousDocumentTop: number | null = null;

  const startedAt = performance.now();

  function stop(): void {
    if (stopped) return;

    stopped = true;
    cancelAnimationFrame(frameId);

    window.removeEventListener('wheel', stopForUserInput);
    window.removeEventListener('touchstart', stopForUserInput);
    window.removeEventListener('pointerdown', stopForUserInput);
    window.removeEventListener('keydown', stopForUserInput);
  }

  function stopForUserInput(event: Event): void {
    // Do not cancel for unrelated keyboard input such as Tab.
    if (event instanceof KeyboardEvent && !SCROLL_KEYS.has(event.key)) {
      return;
    }

    stop();
  }

  function checkPosition(): void {
    if (stopped || !id) return;

    const target = document.getElementById(id);

    if (target) {
      const rect = target.getBoundingClientRect();
      const documentTop = rect.top + window.scrollY;
      const scrollMarginTop =
        Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;

      const targetMoved =
        previousDocumentTop === null || Math.abs(documentTop - previousDocumentTop) > tolerancePx;

      const targetIsMisaligned = Math.abs(rect.top - scrollMarginTop) > tolerancePx;

      if (targetMoved || targetIsMisaligned) {
        // Use auto while correcting layout changes. Repeated smooth
        // animations would introduce another race.
        target.scrollIntoView({
          block: 'start',
          behavior: 'auto'
        });

        previousDocumentTop = documentTop;
      }
    }

    if (performance.now() - startedAt >= timeoutMs) {
      stop();
      return;
    }

    frameId = requestAnimationFrame(checkPosition);
  }

  window.addEventListener('wheel', stopForUserInput, {
    passive: true
  });
  window.addEventListener('touchstart', stopForUserInput, {
    passive: true
  });
  window.addEventListener('pointerdown', stopForUserInput, {
    passive: true
  });
  window.addEventListener('keydown', stopForUserInput);

  frameId = requestAnimationFrame(checkPosition);

  return stop;
}
